import { IComplaintAdapter } from './IComplaintAdapter';
import { ApiResponse } from '../../types/api';
import { Complaint, CreateComplaintDto, ComplaintFilter } from '../../types/complaint';
import { ApiClient } from '../apiClient';
import { retryIfLocked } from '../../utils/retry';
import { hasApiEndpoint } from '../../config/backend';

const MOCK_COMPLAINTS: Complaint[] = [
  {
    complaint_id: 'CMP-2026-00101',
    title: 'AC Cooling Failure in Server Room 101',
    description: 'The primary split AC unit in Server Room 101 stopped cooling and is displaying Error E4. Room temperature rising.',
    department_id: 'DEP-005',
    department_name: 'Information Technology (IT)',
    plant_id: 'PLT-001',
    building_id: 'BLD-001',
    location_id: 'LOC-001',
    location_name: 'Server Room 101',
    category_id: 'CAT-001',
    category_name: 'Air Conditioning & HVAC',
    priority_name: 'Critical',
    status_name: 'In Progress',
    created_by_user_id: 'USR-00001',
    created_by_user_name: 'Alex Mercer',
    created_at: '2026-09-12T10:15:00Z',
    assigned_officer_name: 'Officer Rajesh Kumar',
    assigned_supervisor_name: 'Supervisor Suresh',
    assigned_vendor_name: 'CoolTech HVAC Solutions',
    attachment_urls: ['https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=500'],
  },
  {
    complaint_id: 'CMP-2026-00102',
    title: 'Main Pipe Leakage in Washroom Block B',
    description: 'Water leak detected under main sink on 1st Floor washroom.',
    department_id: 'DEP-004',
    department_name: 'Facilities & Maintenance',
    plant_id: 'PLT-001',
    building_id: 'BLD-001',
    location_id: 'LOC-002',
    location_name: 'Washroom 1st Floor',
    category_id: 'CAT-003',
    category_name: 'Plumbing & Drainage',
    priority_name: 'High',
    status_name: 'New',
    created_by_user_id: 'USR-00002',
    created_by_user_name: 'Sara Khan',
    created_at: '2026-09-12T11:30:00Z',
    attachment_urls: [],
  },
];

export class GASComplaintAdapter implements IComplaintAdapter {
  private complaintsStore: Complaint[] = [...MOCK_COMPLAINTS];

  async getComplaints(filter?: ComplaintFilter): Promise<ApiResponse<Complaint[]>> {
    if (!hasApiEndpoint()) {
      let data = [...this.complaintsStore];
      if (filter?.status && filter.status !== 'ALL') {
        data = data.filter(c => c.status_name.toUpperCase() === filter.status?.toUpperCase());
      }
      if (filter?.priority && filter.priority !== 'ALL') {
        data = data.filter(c => c.priority_name.toUpperCase() === filter.priority?.toUpperCase());
      }
      if (filter?.search) {
        const q = filter.search.toLowerCase();
        data = data.filter(c => c.title.toLowerCase().includes(q) || c.complaint_id.toLowerCase().includes(q));
      }
      return { success: true, data, message: 'Loaded Complaints' };
    }

    return ApiClient.get<Complaint[]>('/complaints');
  }

  async getComplaintById(id: string): Promise<ApiResponse<Complaint>> {
    if (!hasApiEndpoint()) {
      const complaint = this.complaintsStore.find(c => c.complaint_id === id) || this.complaintsStore[0];
      return { success: true, data: complaint, message: 'Complaint Details Loaded' };
    }
    return ApiClient.get<Complaint>(`/complaints/${id}`);
  }

  async createComplaint(dto: CreateComplaintDto): Promise<ApiResponse<Complaint>> {
    if (!hasApiEndpoint()) {
      const newId = `CMP-2026-00${100 + this.complaintsStore.length + 1}`;
      const newComplaint: Complaint = {
        complaint_id: newId,
        title: dto.title,
        description: dto.description,
        department_id: dto.department_id,
        department_name: 'Production & Manufacturing',
        plant_id: dto.plant_id,
        building_id: dto.building_id,
        location_id: dto.location_id,
        location_name: 'Selected Plant Location',
        category_id: dto.category_id,
        category_name: 'Maintenance Category',
        priority_name: dto.priority_name,
        status_name: 'New',
        created_by_user_id: 'USR-00001',
        created_by_user_name: 'Current User',
        created_at: new Date().toISOString(),
        attachment_urls: [],
      };
      this.complaintsStore.unshift(newComplaint);
      return { success: true, data: newComplaint, message: 'Complaint Created Successfully' };
    }

    // GAS writes take a ScriptLock; retry with exponential backoff on lock timeouts.
    return retryIfLocked(() => ApiClient.post<Complaint>('/complaints', dto));
  }

  async updateStatus(id: string, statusName: string): Promise<ApiResponse<Complaint>> {
    if (!hasApiEndpoint()) {
      const complaint = this.complaintsStore.find(c => c.complaint_id === id);
      if (complaint) {
        complaint.status_name = statusName as any;
      }
      return { success: true, data: complaint!, message: 'Status Updated' };
    }
    // GAS writes take a ScriptLock; retry with exponential backoff on lock timeouts.
    return retryIfLocked(() => ApiClient.post<Complaint>(`/complaints/${id}/status`, { statusName }));
  }
}
